import React from "react";
import PropTypes from "prop-types";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { Close, LaunchRounded, MapRounded } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import BackdropOverlay from "../common/BackdropOverlay.jsx";
import { DIALOG_CANCEL_BUTTON_SX, DIALOG_PAPER_RADIUS } from "../common/dialogActionButtonSx";
import { ACCOUNT_PAGE_KYC_CTA_SX } from "../account/accountPageStyles";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";

const MotionBox = motion(Box);
const MOBILE_MQ = `@media ${MOBILE_NAV_MEDIA}`;

export default function CustomMapSubscriptionDialog({
    maps = [],
    onClose,
    onConfirm,
    open,
}) {
    React.useEffect(() => {
        if (!open) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose, open]);

    if (!open) return null;

    return (
        <BackdropOverlay
            open
            onClick={onClose}
            zIndex={1700}
            contentSx={{ width: "100%", maxWidth: "33rem" }}
        >
            <MotionBox
                role="dialog"
                aria-modal="true"
                aria-labelledby="custom-map-dialog-title"
                initial={{ opacity: 0, y: 18, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                sx={{
                    position: "relative",
                    width: "100%",
                    overflow: "hidden",
                    borderRadius: DIALOG_PAPER_RADIUS,
                    border: `1px solid ${alpha(colors.accent.main, 0.28)}`,
                    background: `
                        radial-gradient(circle at 88% 4%, rgba(232,163,61,0.1), transparent 34%),
                        linear-gradient(165deg, rgba(20,17,14,0.995), rgba(10,10,12,0.998) 52%, rgba(7,8,10,1))
                    `,
                    boxShadow: `
                        inset 0 1px 0 rgba(255,232,188,0.08),
                        0 32px 80px rgba(0,0,0,0.68),
                        0 8px 30px rgba(232,163,61,0.08)
                    `,
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "10%",
                        right: "10%",
                        height: "1px",
                        background: `linear-gradient(90deg, transparent, ${alpha(colors.accent.main, 0.72)}, transparent)`,
                        pointerEvents: "none",
                    },
                }}
            >
                <IconButton
                    onClick={onClose}
                    aria-label="Close custom map subscription dialog"
                    sx={{
                        position: "absolute",
                        top: { xs: "0.75rem", sm: "0.9rem" },
                        right: { xs: "0.75rem", sm: "0.9rem" },
                        zIndex: 2,
                        width: "1.9rem",
                        height: "1.9rem",
                        borderRadius: "0.55rem",
                        color: colors.text.secondary,
                        backgroundColor: "rgba(255,255,255,0.04)",
                        "&:hover": {
                            color: colors.text.primary,
                            backgroundColor: "rgba(255,255,255,0.08)",
                        },
                    }}
                >
                    <Close sx={{ fontSize: "0.95rem" }} />
                </IconButton>

                <Box
                    sx={{
                        px: { xs: "1.25rem", sm: "1.7rem" },
                        pt: { xs: "1.3rem", sm: "1.65rem" },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.8rem",
                            pr: "2.5rem",
                        }}
                    >
                        <Box
                            aria-hidden
                            sx={{
                                width: "2.5rem",
                                height: "2.5rem",
                                flexShrink: 0,
                                display: "grid",
                                placeItems: "center",
                                borderRadius: "10px",
                                color: colors.accent.main,
                                border: `1px solid ${alpha(colors.accent.main, 0.28)}`,
                                background: alpha(colors.accent.main, 0.1),
                                boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.06)}`,
                            }}
                        >
                            <MapRounded sx={{ fontSize: "1.2rem" }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    color: colors.accent.soft,
                                    fontSize: "0.64rem",
                                    fontWeight: 800,
                                    letterSpacing: "0.14em",
                                    textTransform: "uppercase",
                                }}
                            >
                                Steam Workshop required
                            </Typography>
                            <Typography
                                id="custom-map-dialog-title"
                                component="h2"
                                sx={{
                                    mt: "0.2rem",
                                    color: colors.text.primary,
                                    fontSize: { xs: "1.05rem", sm: "1.2rem" },
                                    fontWeight: 750,
                                    letterSpacing: "0.015em",
                                    lineHeight: 1.2,
                                }}
                            >
                                Prepare your custom maps
                            </Typography>
                        </Box>
                    </Box>

                    <Typography
                        sx={{
                            mt: "1.2rem",
                            color: colors.text.secondary,
                            fontSize: { xs: "0.78rem", sm: "0.84rem" },
                            lineHeight: 1.55,
                        }}
                    >
                        Subscribe to and download each selected Workshop map before
                        entering matchmaking. Confirm once every map is ready locally.
                    </Typography>

                    <Box
                        sx={{
                            mt: "1.15rem",
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.55rem",
                            maxHeight: "15rem",
                            overflowY: "auto",
                            pr: "0.15rem",
                        }}
                    >
                        {maps.map((map, index) => (
                            <Box
                                key={map.workshopItemId || map.mapDisplayName}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.8rem",
                                    minHeight: "3.25rem",
                                    px: "0.85rem",
                                    py: "0.6rem",
                                    borderRadius: "10px",
                                    border: `1px solid ${alpha(colors.text.primary, 0.07)}`,
                                    background: "rgba(255,255,255,0.025)",
                                }}
                            >
                                <Typography
                                    aria-hidden
                                    sx={{
                                        color: alpha(colors.accent.main, 0.72),
                                        fontFamily: "'Orbitron', sans-serif",
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                        minWidth: "1.35rem",
                                    }}
                                >
                                    {String(index + 1).padStart(2, "0")}
                                </Typography>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            color: colors.text.primary,
                                            fontSize: "0.82rem",
                                            fontWeight: 700,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {map.mapDisplayName}
                                    </Typography>
                                    {!map.workshopUrl ? (
                                        <Typography
                                            sx={{
                                                mt: "0.1rem",
                                                color: colors.text.muted,
                                                fontSize: "0.66rem",
                                            }}
                                        >
                                            Workshop ID: {map.workshopItemId}
                                        </Typography>
                                    ) : null}
                                </Box>
                                {map.workshopUrl ? (
                                    <Box
                                        component="a"
                                        href={map.workshopUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "0.35rem",
                                            flexShrink: 0,
                                            color: colors.accent.soft,
                                            fontSize: "0.68rem",
                                            fontWeight: 700,
                                            textDecoration: "none",
                                            "&:hover": { color: colors.primary.main },
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{ display: { xs: "none", sm: "inline" } }}
                                        >
                                            Open Workshop
                                        </Box>
                                        <LaunchRounded sx={{ fontSize: "0.9rem" }} />
                                    </Box>
                                ) : null}
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box
                    sx={{
                        mt: "1.45rem",
                        px: { xs: "1.25rem", sm: "1.7rem" },
                        py: { xs: "1rem", sm: "1.15rem" },
                        borderTop: `1px solid ${colors.border.default}`,
                        background: "rgba(0,0,0,0.18)",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: "25.25rem",
                            mx: "auto",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "0.75rem",
                            [MOBILE_MQ]: {
                                flexDirection: "column",
                                maxWidth: "100%",
                            },
                        }}
                    >
                        <Button
                            onClick={onClose}
                            sx={{
                                ...DIALOG_CANCEL_BUTTON_SX,
                                flex: { xs: "none", sm: "0 0 9.5rem" },
                                width: { sm: "9.5rem" },
                                minWidth: { sm: "9.5rem" },
                                maxWidth: { sm: "9.5rem" },
                                [MOBILE_MQ]: {
                                    width: "100%",
                                    minWidth: 0,
                                    maxWidth: "100%",
                                    height: "2.35rem",
                                    borderRadius: "6px",
                                },
                            }}
                        >
                            Not now
                        </Button>
                        <Button
                            onClick={onConfirm}
                            sx={{
                                ...ACCOUNT_PAGE_KYC_CTA_SX,
                                flex: { xs: "none", sm: "0 0 15rem" },
                                width: { sm: "15rem" },
                                minWidth: { sm: "15rem" },
                                maxWidth: { sm: "15rem" },
                                minHeight: "2.5rem",
                                height: "2.5rem",
                                px: "0.75rem",
                                fontSize: { xs: "0.72rem", sm: "0.78rem" },
                                textTransform: "none",
                                [MOBILE_MQ]: {
                                    width: "100%",
                                    minWidth: 0,
                                    maxWidth: "100%",
                                    minHeight: "2.35rem",
                                    height: "2.35rem",
                                    borderRadius: "6px",
                                },
                            }}
                        >
                            I'm already subscribed
                        </Button>
                    </Box>
                </Box>
            </MotionBox>
        </BackdropOverlay>
    );
}

CustomMapSubscriptionDialog.propTypes = {
    maps: PropTypes.arrayOf(
        PropTypes.shape({
            mapDisplayName: PropTypes.string,
            workshopItemId: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            workshopUrl: PropTypes.string,
        }),
    ),
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    open: PropTypes.bool,
};